import _mergeJSXProps from "@vue/babel-helper-vue-jsx-merge-props";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { isArray, isEqual, isFunction, debounce } from '@cloud-sn/v-utils';
import { buildComponentName } from '../utils';
import { getDragNodeKeys, calcDragOverGap } from './utils';
import InnerNode from './innerNode';
import StoreMixin from './mixins/store';
import AsyncMixin from './mixins/async';
import ApiMixin from './mixins/api';
export default {
  name: buildComponentName('Tree'),
  provide: function provide() {
    return {
      treeRoot: this
    };
  },
  mixins: [StoreMixin, AsyncMixin, ApiMixin],
  props: {
    prefixCls: {
      type: String,
      default: 'ux-tree'
    },
    dataSource: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    selectedKeys: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    checkedKeys: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    expandedKeys: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rowKey: {
      type: [String, Function],
      default: undefined
    },
    showLine: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: true
    },
    checkable: {
      type: Boolean,
      default: false
    },
    checkStrict: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    defaultExpandAll: {
      type: Boolean,
      default: false
    },
    defaultExpandParent: {
      type: Boolean,
      default: true
    },
    lazy: {
      type: Boolean,
      default: false
    },
    loadData: {
      type: Function,
      default: function _default() {
        return Promise.resolve();
      }
    },
    renderContent: {
      type: Function,
      default: null
    },
    filterOption: {
      type: Function,
      default: null
    },
    directory: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    allowDrag: {
      type: Function,
      default: null
    },
    allowDrop: {
      type: Function,
      default: null
    }
  },
  data: function data() {
    return {
      nodes: [],
      dragNode: null,
      // 当前拖拽包含所有节点key
      dragNodeKeys: [],
      dragOverNode: null,
      dropPosition: null,
      dragEnterTimer: null
    };
  },
  computed: {
    classes: function classes() {
      var _ref;

      var prefixCls = this.prefixCls,
          showLine = this.showLine,
          directory = this.directory,
          draggable = this.draggable;
      return _ref = {}, _defineProperty(_ref, prefixCls, true), _defineProperty(_ref, "".concat(prefixCls, "-directory"), directory), _defineProperty(_ref, "".concat(prefixCls, "-show-line"), showLine), _defineProperty(_ref, "".concat(prefixCls, "-draggable"), draggable), _ref;
    },
    filterNodes: function filterNodes() {
      var nodes = this.nodes;
      return nodes;
    },
    renderContentFn: function renderContentFn() {
      var $scopedSlots = this.$scopedSlots,
          renderContent = this.renderContent;
      return $scopedSlots.renderContent || renderContent;
    }
  },
  watch: {
    dataSource: function dataSource(nVal) {
      if (!isArray(nVal)) return;
      var clearNodesMap = this.clearNodesMap,
          createNodes = this.createNodes,
          triggerAsyncEvent = this.triggerAsyncEvent;
      clearNodesMap();
      this.nodes = createNodes(nVal, null, 0);
      triggerAsyncEvent();
    },
    selectedKeys: function selectedKeys(nVal, oVal) {
      if (!isArray(nVal) || isEqual(nVal, oVal)) return;
      this.updateStoreSelectedKeys(nVal, null, true);
    },
    checkedKeys: function checkedKeys(nVal, oVal) {
      if (!isArray(nVal) || isEqual(nVal, oVal)) return;
      this.updateStoreCheckedKeys(nVal, null, true);
    },
    expandedKeys: function expandedKeys(nVal, oVal) {
      if (!isArray(nVal) || isEqual(nVal, oVal)) return;
      this.updateStoreExpandedKeys(nVal, null, true);
      this.callHook('expandedKeysChanged', nVal);
    }
  },
  created: function created() {
    var selectedKeys = this.selectedKeys,
        checkedKeys = this.checkedKeys,
        expandedKeys = this.expandedKeys,
        updateStoreSelectedKeys = this.updateStoreSelectedKeys,
        updateStoreExpandedKeys = this.updateStoreExpandedKeys,
        updateStoreCheckedKeys = this.updateStoreCheckedKeys,
        loadRootNodes = this.loadRootNodes,
        triggerCheckedKeysChangeEvent = this.triggerCheckedKeysChangeEvent,
        triggerSelectedKeysChangeEvent = this.triggerSelectedKeysChangeEvent,
        triggerAsyncEvent = this.triggerAsyncEvent;
    this.debounceTriggerCheckedKeysChangeEvent = debounce(triggerCheckedKeysChangeEvent, 20);
    this.debounceTriggerSelectedKeysChangeEvent = debounce(triggerSelectedKeysChangeEvent, 20);
    updateStoreSelectedKeys(selectedKeys, null, true);
    updateStoreCheckedKeys(checkedKeys, null, true);
    updateStoreExpandedKeys(expandedKeys, null, true);
    loadRootNodes().then(triggerAsyncEvent);
  },
  methods: {
    loadRootNodes: function loadRootNodes() {
      var _this = this;

      var dataSource = this.dataSource,
          createNodes = this.createNodes,
          asyncNode = this.asyncNode,
          canAsync = this.canAsync;
      return new Promise(function (resolve) {
        if (dataSource && dataSource.length > 0) {
          _this.nodes = createNodes(dataSource, null, 0);
        } else if (canAsync()) {
          asyncNode().then(function (nodes) {
            _this.nodes = nodes || [];
            resolve();
          });
        } else {
          resolve();
        }
      });
    },
    searchFilter: function searchFilter(value, node) {
      var filterOption = this.filterOption;

      if (isFunction(filterOption)) {
        return filterOption(value, node);
      }

      return (String(node.key) || '').indexOf(value) > -1;
    },
    onNodeClick: function onNodeClick(e, node, vm) {
      this.$emit('node-click', e, _objectSpread({}, node.originNode), vm);
    },
    onNodeSelect: function onNodeSelect(e, node, vm) {
      var multiple = this.multiple,
          updateStoreSelectedKeys = this.updateStoreSelectedKeys,
          getStoreSelectedKeys = this.getStoreSelectedKeys,
          getStoreSelectedNodes = this.getStoreSelectedNodes;
      var key = node.key,
          isSelected = node.isSelected;
      updateStoreSelectedKeys(key, isSelected ? 'add' : 'del', isSelected && !multiple);
      this.$emit('select', getStoreSelectedKeys(), {
        selected: isSelected,
        selectedNodes: getStoreSelectedNodes(),
        node: _objectSpread({}, node.originNode),
        vm: vm,
        domEvent: e
      });
    },
    onNodeCheck: function onNodeCheck(e, node, vm) {
      var setStoreCheckedKeys = this.setStoreCheckedKeys,
          setStoreHalfCheckedKeys = this.setStoreHalfCheckedKeys,
          checkNodeRelation = this.checkNodeRelation,
          getStoreCheckedKeys = this.getStoreCheckedKeys,
          getStoreHalfCheckedKeys = this.getStoreHalfCheckedKeys,
          getStoreCheckedNodes = this.getStoreCheckedNodes;
      var key = node.key,
          isChecked = node.isChecked,
          isHalfChecked = node.isHalfChecked;
      setStoreCheckedKeys(key, isChecked);
      setStoreHalfCheckedKeys(key, isHalfChecked);
      checkNodeRelation(node);
      this.$emit('check', getStoreCheckedKeys(), {
        checked: isChecked,
        checkedNodes: getStoreCheckedNodes(),
        halfCheckedkeys: getStoreHalfCheckedKeys(),
        node: _objectSpread({}, node.originNode),
        vm: vm,
        domEvent: e
      });
    },
    triggerExpand: function triggerExpand(e, node, vm) {
      var updateStoreExpandedKeys = this.updateStoreExpandedKeys,
          getStoreExpandedKeys = this.getStoreExpandedKeys,
          getStoreExpandedNodes = this.getStoreExpandedNodes;
      var key = node.key,
          isExpanded = node.isExpanded;
      updateStoreExpandedKeys(key, isExpanded ? 'add' : 'del');
      this.$emit('expand', getStoreExpandedKeys(), {
        expanded: isExpanded,
        expandedNodes: getStoreExpandedNodes(),
        node: _objectSpread({}, node.originNode),
        vm: vm,
        domEvent: e
      });
    },
    onNodeExpand: function onNodeExpand(e, node, vm) {
      var canAsync = this.canAsync,
          asyncNode = this.asyncNode,
          triggerExpand = this.triggerExpand,
          triggerAsyncEvent = this.triggerAsyncEvent,
          callHook = this.callHook;
      var isExpanded = node.isExpanded;
      callHook('onNodeExpandBeforeHook', e, node, vm);

      if (canAsync(node)) {
        if (isExpanded) {
          asyncNode(node).then(function () {
            if (node.isParent) {
              triggerExpand(e, node, vm);
            }

            triggerAsyncEvent();
          });
        }
      } else {
        triggerExpand(e, node, vm);
      }
    },
    onNodeDragStart: function onNodeDragStart(e, node) {
      var originNode = _objectSpread({}, node.originNode);

      this.dragNode = node;
      this.dragNodeKeys = getDragNodeKeys([node]);
      this.$emit('dragstart', {
        e: e,
        node: originNode
      });
    },
    clearDragOverGap: function clearDragOverGap(dragOverNode) {
      var nDragOverNode = dragOverNode;

      if (nDragOverNode) {
        nDragOverNode.dragOverGap = 'none';
      }
    },
    onNodeDragEnd: function onNodeDragEnd(e, node) {
      var clearDragOverGap = this.clearDragOverGap,
          dragOverNode = this.dragOverNode,
          callHook = this.callHook;
      clearDragOverGap(dragOverNode);
      this.$emit('dragend', {
        e: e,
        node: _objectSpread({}, node.originNode)
      });
      callHook('onNodeDragEndHook', e, node);
    },
    clearDragEnterTimer: function clearDragEnterTimer() {
      if (this.dragEnterTimer) {
        clearTimeout(this.dragEnterTimer);
      }
    },
    triggerDragExpand: function triggerDragExpand(e) {
      var node = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var vm = arguments.length > 2 ? arguments[2] : undefined;
      // 因为drop之后重新生成了node,所以需要重新获取
      var nNode = this.getStoreNode(node.key);
      nNode.isExpanded = nNode.children && nNode.children.length > 0;
      this.triggerExpand(e, nNode, vm);
    },
    onNodeDragEnter: function onNodeDragEnter(e, node, vm) {
      var _this2 = this;

      var canAsync = this.canAsync;
      this.clearDragEnterTimer();

      if (!canAsync(node)) {
        this.dragEnterTimer = setTimeout(function () {
          // 因为drop之后重新生成了node,所以需要重新获取
          _this2.triggerDragExpand(e, node, vm);

          _this2.$emit('dragenter', {
            e: e,
            // drop时直接操作的的是node的originNode,所以数据时正确的
            node: _objectSpread({}, node.originNode),
            expandedKeys: _this2.getStoreExpandedKeys()
          });
        }, 500);
      }
    },
    onNodeDragLeave: function onNodeDragLeave(e, node) {
      this.clearDragOverGap(node);
      this.$emit('dragleave', {
        e: e,
        node: _objectSpread({}, node.originNode)
      });
    },
    onNodeDragOver: function onNodeDragOver(e, node, _ref2) {
      var selectorRef = _ref2.$refs.selectorRef;
      var dragNode = this.dragNode,
          clearDragOverGap = this.clearDragOverGap,
          oldDragOverNode = this.dragOverNode;

      if (!dragNode) {
        this.dragOverNode = null;
        return;
      }

      clearDragOverGap(oldDragOverNode);
      var dragOverNode = node;
      dragOverNode.dragOverGap = calcDragOverGap(e, selectorRef);
      this.dragOverNode = dragOverNode;
      this.$emit('dragover', {
        e: e,
        node: _objectSpread({}, node.originNode)
      });
    },
    isDropInSelf: function isDropInSelf(dropNode) {
      var _this$dragNodeKeys = this.dragNodeKeys,
          dragNodeKeys = _this$dragNodeKeys === void 0 ? [] : _this$dragNodeKeys;
      var key = dropNode.key;
      return dragNodeKeys.indexOf(key) > -1;
    },
    onNodeDrop: function onNodeDrop(e, dropNode, vm) {
      var that = this;
      var dragNode = that.dragNode,
          dragOverNode = that.dragOverNode,
          dragNodeKeys = that.dragNodeKeys,
          isDropInSelf = that.isDropInSelf,
          addChildren = that.addChildren,
          setChildren = that.setChildren,
          removeChildren = that.removeChildren,
          createNodes = that.createNodes,
          onNodeExpand = that.onNodeExpand,
          getStoreNode = that.getStoreNode,
          clearDragEnterTimer = that.clearDragEnterTimer,
          updateStoreExpandedKeys = that.updateStoreExpandedKeys;
      var nDropNode = dropNode; // const originNode = { ...nDropNode.originNode };

      var isAllowDrop = !isDropInSelf(nDropNode);

      if (!isAllowDrop) {
        e.preventDefault();
        return;
      }

      if (dragNode) {
        clearDragEnterTimer();
        var treeData = this.nodes.map(function (v) {
          return v.originNode;
        });
        var dragParent = dragNode.parent;
        var dragParentChilds = !dragParent ? {
          set children(childs) {
            treeData = childs;
          },

          get children() {
            return treeData;
          }

        } : {
          set children(childs) {
            dragParent.originNode.children = childs;
          },

          get children() {
            return dragParent.originNode.children;
          }

        };
        removeChildren(dragParentChilds, dragNode.originNode); // 同步当前父级展开状态

        if (dragParent && (!dragParentChilds.children || dragParentChilds.children.length === 0)) {
          updateStoreExpandedKeys(dragParent.key, 'del');
        }

        var dropParent = nDropNode.parent;
        var dropParentChilds = !dropParent ? {
          set children(childs) {
            treeData = childs;
          },

          get children() {
            return treeData;
          }

        } : {
          set children(childs) {
            dropParent.originNode.children = childs;
          },

          get children() {
            return dropParent.originNode.children;
          }

        };
        var dragOverGap = dragOverNode.dragOverGap;
        var dropChilds = {
          set children(childs) {
            nDropNode.originNode.children = childs;
          },

          get children() {
            return nDropNode.originNode.children;
          }

        };

        if (dragOverGap === 'mid') {
          addChildren(dropChilds, dragNode.originNode);
        } else if (dragOverGap === 'bottom' && dropChilds.children && dropChilds.children.length > 0 && nDropNode.isExpanded) {
          setChildren(dropChilds, dragNode.originNode, null, 0);
        } else {
          var idx = dropParentChilds.children.indexOf(nDropNode.originNode);
          setChildren(dropParentChilds, dragNode.originNode, null, dragOverGap === 'top' ? idx : idx + 1);
        }

        this.nodes = createNodes(treeData || this.nodes.map(function (v) {
          return v.originNode;
        }), null, 0, true, 'drop');

        if (dragOverGap === 'mid') {
          var newDropNode = getStoreNode(nDropNode.key);
          newDropNode.isExpanded = true;
          onNodeExpand(e, newDropNode, vm);
        } else if (dropParent) {
          this.triggerDragExpand(e, dropParent, vm);
        }

        this.$emit('drop', {
          e: e,
          dragOverGap: dragOverGap,
          node: _objectSpread({}, dropNode.originNode),
          dragNode: _objectSpread({}, dragNode.originNode),
          dragNodeKeys: dragNodeKeys
        });
      }
    },
    triggerAsyncEvent: function triggerAsyncEvent() {
      var debounceTriggerCheckedKeysChangeEvent = this.debounceTriggerCheckedKeysChangeEvent,
          debounceTriggerSelectedKeysChangeEvent = this.debounceTriggerSelectedKeysChangeEvent;
      debounceTriggerCheckedKeysChangeEvent({
        keyName: 'checkedKeys',
        op: null,
        replace: true
      });
      debounceTriggerSelectedKeysChangeEvent({
        keyName: 'selectedKeys',
        op: null,
        replace: true
      });
    },
    triggerSelectedKeysChangeEvent: function triggerSelectedKeysChangeEvent(e) {
      var getStoreSelectedKeys = this.getStoreSelectedKeys,
          getStoreSelectedNodes = this.getStoreSelectedNodes;
      this.$emit('selected-keys-change', getStoreSelectedKeys(), _objectSpread({}, e, {
        nodes: getStoreSelectedNodes()
      }));
    },
    triggerCheckedKeysChangeEvent: function triggerCheckedKeysChangeEvent(e) {
      var getStoreCheckedKeys = this.getStoreCheckedKeys,
          getStoreCheckedNodes = this.getStoreCheckedNodes;
      this.$emit('checked-keys-change', getStoreCheckedKeys(), _objectSpread({}, e, {
        nodes: getStoreCheckedNodes()
      }));
    },
    callHook: function callHook() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      var fnName = args.shift();

      if (this[fnName]) {
        this[fnName].apply(this, args);
      }
    }
  },
  render: function render() {
    var h = arguments[0];
    var prefixCls = this.prefixCls,
        filterNodes = this.filterNodes,
        classes = this.classes;
    var childrens = filterNodes.map(function (node) {
      return h(InnerNode, {
        "props": _objectSpread({}, {
          prefixCls: prefixCls,
          node: node
        })
      });
    });
    return h("ul", _mergeJSXProps([{}, {
      class: classes,
      attrs: {
        unselectable: 'on'
      }
    }]), [childrens]);
  }
};